import {
  addDays,
  differenceInCalendarDays,
  endOfDay,
  format,
  isValid,
  parse,
  startOfDay,
} from 'date-fns';
import moment from 'moment';
import { getDateFormat } from '../../../_lib/lib';
import { IOfferTimeRange } from '../../../types/IItemSetup';
import { dateFormats } from '../../PreOrders/Utils';

export const commonDateFormat = 'MM/DD/yyyy';
export const lifecycleDateFormat = 'MM/DD/yyyy (hh:mm a)';
const displayDateFormat = 'MM/dd/yyyy';
const payloadDateTimeFormat = 'yyyy-MM-DD HH:mm:ss';

export interface IStartEndDate {
  startDate: string;
  endDate: string;
}

export interface IFilterOptions {
  offerDates: IStartEndDate | null;
  shippingDates: IStartEndDate | null;
}

export interface IShowNoDate {
  offerStart: boolean;
  offerEnd: boolean;
  shippingStart: boolean;
  shippingEnd: boolean;
}

export interface ILifecycleDates {
  negotiatingDates: { startDate?: moment.Moment; endDate?: moment.Moment };
  finalizingDates: { startDate?: moment.Moment; endDate?: moment.Moment };
}

export interface ICreateDatesRangeParams {
  offerDates?: IStartEndDate | null;
  shippingDates?: IStartEndDate | null;
  daysToFinalize: number;
}

const toMoment = (value: any) =>
  moment.isMoment(value) ? moment(value) : moment(value, commonDateFormat);

export const getDaysBetween = (from: any, to: any) => toMoment(to).diff(toMoment(from), 'days');

export const parseCreateDateInput = (value?: string | null): Date | null => {
  if (!value) return null;
  const dateFormat = getDateFormat(value);
  if (!dateFormat) return null;
  const parsedDate = parse(value, dateFormat, new Date());
  return isValid(parsedDate) ? parsedDate : null;
};

export const getCreateDatesRangeError = ({
  offerDates,
  shippingDates,
  daysToFinalize,
}: ICreateDatesRangeParams): string => {
  const offerStartDate = parseCreateDateInput(offerDates?.startDate);
  const offerEndDate = parseCreateDateInput(offerDates?.endDate);
  const shippingStartDate = parseCreateDateInput(shippingDates?.startDate);
  const shippingEndDate = parseCreateDateInput(shippingDates?.endDate);

  if (!offerStartDate || !offerEndDate || !shippingStartDate || !shippingEndDate)
    return 'Enter all offer and shipping dates in the MM/DD/YYYY format';

  const currentDate = startOfDay(new Date());
  if (differenceInCalendarDays(offerStartDate, currentDate) < 0)
    return `Offer start date cannot be before ${format(currentDate, displayDateFormat)}`;

  if (differenceInCalendarDays(offerEndDate, offerStartDate) < 0)
    return 'Offer end date cannot be before the offer start date';

  const earliestShippingStartDate = addDays(offerEndDate, daysToFinalize + 2);
  if (differenceInCalendarDays(shippingStartDate, earliestShippingStartDate) < 0)
    return `Shipping start date cannot be before ${format(
      earliestShippingStartDate,
      displayDateFormat
    )}, a ${
      daysToFinalize + 1
    }-day gap is needed after the offer end date for Negotiating and Finalizing`;

  if (differenceInCalendarDays(shippingEndDate, shippingStartDate) < 1)
    return 'Shipping end date must be after the shipping start date';

  return '';
};

export const getInitialCreateDatesState = (customOptionObj: any) => {
  const offerStartDate = customOptionObj.offerDates.startDate
    ? moment(customOptionObj.offerDates.startDate)
    : '';
  const offerEndDate = customOptionObj.offerDates.endDate
    ? moment(customOptionObj.offerDates.endDate)
    : '';
  const shippingStartDate = customOptionObj.shippingDates.startDate
    ? moment(customOptionObj.shippingDates.startDate)
    : '';
  const shippingEndDate = customOptionObj.shippingDates.endDate
    ? moment(customOptionObj.shippingDates.endDate)
    : '';

  const hasEveryDate = !!(offerStartDate && offerEndDate && shippingStartDate && shippingEndDate);

  return {
    filterOptions: {
      offerDates: {
        startDate: offerStartDate ? offerStartDate.format(commonDateFormat) : '',
        endDate: offerEndDate ? offerEndDate.format(commonDateFormat) : '',
      },
      shippingDates: {
        startDate: shippingStartDate ? shippingStartDate.format(commonDateFormat) : '',
        endDate: shippingEndDate ? shippingEndDate.format(commonDateFormat) : '',
      },
    },
    disabledDates: hasEveryDate
      ? {
          offerDates: {
            before: (offerStartDate as moment.Moment).format(commonDateFormat),
            after: (offerEndDate as moment.Moment).format(commonDateFormat),
          },
          shippingDates: {
            before: (shippingStartDate as moment.Moment).format(commonDateFormat),
            after: (shippingEndDate as moment.Moment).format(commonDateFormat),
          },
        }
      : null,
  };
};

export const getNegotiatingStartDate = (
  offerEndDate: string | undefined,
  offeringTimeRange: IOfferTimeRange
) =>
  moment(offerEndDate, commonDateFormat).add(
    offeringTimeRange?.offerEndTimeInSeconds + 1,
    'seconds'
  );

export const getLifecycleDates = ({
  filterOptions,
  showNoDate,
  offeringTimeRange,
  daysToFinalize,
}: {
  filterOptions: IFilterOptions;
  showNoDate: IShowNoDate;
  offeringTimeRange: IOfferTimeRange;
  daysToFinalize: number;
}): ILifecycleDates => {
  let negotiatingStartDate, negotiatingEndDate, finalizingStartDate, finalizingEndDate;
  if (!showNoDate.offerEnd) {
    negotiatingStartDate = getNegotiatingStartDate(
      filterOptions.offerDates?.endDate,
      offeringTimeRange
    );
  }
  if (!showNoDate.shippingStart) {
    finalizingStartDate = moment(filterOptions.shippingDates?.startDate, commonDateFormat)
      .subtract(daysToFinalize + 1, 'days')
      .add(offeringTimeRange?.offerEndTimeInSeconds + 1, 'seconds');

    negotiatingEndDate = moment(finalizingStartDate).subtract(1, 'second');
    finalizingEndDate = moment(filterOptions.shippingDates?.startDate, commonDateFormat).subtract(
      1,
      'second'
    );
  }
  return {
    negotiatingDates: { startDate: negotiatingStartDate, endDate: negotiatingEndDate },
    finalizingDates: { startDate: finalizingStartDate, endDate: finalizingEndDate },
  };
};

export const getEarliestShippingStartDate = (offerEndDate: string, daysToFinalize: number) =>
  moment(offerEndDate, commonDateFormat)
    .add(daysToFinalize + 2, 'day')
    .format(commonDateFormat);

export const getAdjustedShippingStartDate = ({
  offerEndDate,
  shippingStartDate,
  daysToFinalize,
}: {
  offerEndDate: string;
  shippingStartDate?: string;
  daysToFinalize: number;
}): string | undefined => {
  const gapEndDate = moment(offerEndDate, commonDateFormat).add(daysToFinalize + 1, 'days');
  if (shippingStartDate && moment(shippingStartDate, commonDateFormat).isSameOrBefore(gapEndDate))
    return getEarliestShippingStartDate(offerEndDate, daysToFinalize);
  return undefined;
};

export const getShippingStartDisabledBefore = ({
  offerEndDate,
  adjustedShippingStartDate,
  shippingDisabledBefore,
  overLappingShippingDates,
  daysToFinalize,
}: {
  offerEndDate: string;
  adjustedShippingStartDate?: string;
  shippingDisabledBefore?: string;
  overLappingShippingDates?: string;
  daysToFinalize: number;
}): string => {
  if (adjustedShippingStartDate) return adjustedShippingStartDate;

  const earliestShippingStartDate = getEarliestShippingStartDate(offerEndDate, daysToFinalize);
  if (!overLappingShippingDates) return earliestShippingStartDate;

  const gapEndDate = moment(offerEndDate, commonDateFormat).add(daysToFinalize + 1, 'days');
  if (
    moment(shippingDisabledBefore, commonDateFormat).isSameOrBefore(gapEndDate) ||
    gapEndDate.isSameOrAfter(overLappingShippingDates)
  )
    return earliestShippingStartDate;

  return moment(overLappingShippingDates).format(commonDateFormat);
};

export const buildCreateDatesPayload = ({
  offerDates,
  shippingDates,
  lifecycleDates,
}: {
  offerDates?: IStartEndDate | null;
  shippingDates?: IStartEndDate | null;
  lifecycleDates: ILifecycleDates;
}) => {
  const offerStartDateFormat = getDateFormat(offerDates?.startDate || '');
  const offerEndDateFormat = getDateFormat(offerDates?.endDate || '');
  const shippingStartDateFormat = getDateFormat(shippingDates?.startDate || '');
  const shippingEndDateFormat = getDateFormat(shippingDates?.endDate || '');

  if (
    !offerStartDateFormat ||
    !offerEndDateFormat ||
    !shippingStartDateFormat ||
    !shippingEndDateFormat
  )
    return null;

  const offerStartDate = startOfDay(
    parse(offerDates?.startDate ?? '', offerStartDateFormat, new Date())
  );
  const offerEndDate = endOfDay(parse(offerDates?.endDate ?? '', offerEndDateFormat, new Date()));
  const shippingStartDate = startOfDay(
    parse(shippingDates?.startDate ?? '', shippingStartDateFormat, new Date())
  );
  const shippingEndDate = endOfDay(
    parse(shippingDates?.endDate ?? '', shippingEndDateFormat, new Date())
  );

  return {
    offerStartDate: format(offerStartDate, dateFormats.yearMonthDate),
    offerEndDate: format(offerEndDate, dateFormats.yearMonthDate),
    shippingStartDate: format(shippingStartDate, dateFormats.yearMonthDate),
    shippingEndDate: format(shippingEndDate, dateFormats.yearMonthDate),
    negotiatingStartDate: lifecycleDates.negotiatingDates?.startDate?.format(payloadDateTimeFormat),
    negotiatingEndDate: lifecycleDates.negotiatingDates?.endDate?.format(payloadDateTimeFormat),
    finalizingStartDate: lifecycleDates.finalizingDates?.startDate?.format(payloadDateTimeFormat),
    finalizingEndDate: lifecycleDates.finalizingDates?.endDate?.format(payloadDateTimeFormat),
  };
};
